import { normalizeFigureKey } from './loadingStateKeys';
import { isAiGeneratedRasterFigure } from './mediaReviewUtils';
import type { InstructionStepDto, StepRasterFigureDto } from './types';

export type RasterSlotKind = 'pending' | 'generating' | 'awaiting' | 'ready' | 'failed';

export type SupportingMaterialContentSegment =
  | { type: 'markdown'; content: string }
  | {
      type: 'figure';
      key: string;
      spec: string;
      svg: string;
      failed: boolean;
    }
  | {
      type: 'raster';
      key: string;
      src: string;
      alt: string;
      aiGenerated?: boolean;
    }
  | {
      type: 'raster_slot';
      key: string;
      kind: RasterSlotKind;
      src?: string;
      alt?: string;
    };

const FIGURE_BLOCK_RE =
  /<!--\s*FIGURE_DIAGRAM:key=([^\r\n]+?)\s*-->[\s\S]*?<!--\s*\/FIGURE_DIAGRAM\s*-->/gi;
const RASTER_BLOCK_RE =
  /<!--\s*FIGURE_RASTER:key=([^\r\n]+?)\s*-->[\s\S]*?<!--\s*\/FIGURE_RASTER\s*-->/gi;
const TEMP_SLOT_RE =
  /<!--\s*FIGURE_(GENERATING|AWAITING|EXTRACTION_FAILED):key=([^\r\n]+?)\s*-->[\s\S]*?<!--\s*\/FIGURE_\1\s*-->/gi;
const PENDING_MARKER_RE = /<!--\s*FIGURE_PENDING:key=([^\r\n]+?)\s*-->/gi;
const KEYED_SLOT_RE =
  /<!--\s*FIGURE_(GENERATING|AWAITING|RASTER|EXTRACTION_FAILED):key=([^\r\n]+?)\s*-->[\s\S]*?<!--\s*\/FIGURE_\1\s*-->/gi;
const HIDDEN_SPEC_RE = /<!--\s*FIGURE_DIAGRAM_SPEC\s*\n([\s\S]*?)\n\s*-->/i;
const SVG_BLOCK_RE = /<svg\b[\s\S]*?<\/svg>/i;
const IMG_TAG_RE = /<img\b([^>]*)>/i;

function extractSvg(markup: string): string {
  const match = markup.match(SVG_BLOCK_RE);
  return match?.[0] ?? '';
}

function extractHiddenSpec(markup: string): string {
  const match = markup.match(HIDDEN_SPEC_RE);
  const raw = match?.[1]?.trim() ?? '';
  if (!raw) return '';
  const specMatch = raw.match(/\[DIAGRAM_SPEC\][\s\S]*?\[\/DIAGRAM_SPEC\]/i);
  return specMatch?.[0] ?? raw;
}

function isFailedSvg(svg: string): boolean {
  // Mirror backend LessonFigureDiagramMarkup: text-only placeholders are not failures.
  if (/data-text-only\s*=\s*["']true["']/i.test(svg)) return false;
  return /data-diagram-failed\s*=\s*["']true["']/i.test(svg);
}

function decodeAttr(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function readImgAttr(attrs: string, name: string): string {
  const match = attrs.match(new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i'));
  return match ? decodeAttr(match[1]) : '';
}

function extractRasterImg(markup: string): { src: string; alt: string; aiGenerated: boolean } {
  const match = markup.match(IMG_TAG_RE);
  if (!match) return { src: '', alt: '', aiGenerated: false };
  const attrs = match[1];
  const aiFlag = readImgAttr(attrs, 'data-ai-generated').trim().toLowerCase();
  return {
    src: readImgAttr(attrs, 'src'),
    alt: readImgAttr(attrs, 'alt'),
    aiGenerated: aiFlag === 'true' || aiFlag === '1',
  };
}

export function isTypedFigureSpec(spec: string | undefined): boolean {
  return /Typed:\s*true/i.test(spec ?? '');
}

export function buildRasterFigureMarkdownBlock(
  key: string,
  src: string,
  alt?: string,
  options?: { aiGenerated?: boolean }
): string {
  const normalizedKey = normalizeFigureKey(key) || key.trim();
  const safeSrc = src.replace(/"/g, '&quot;');
  const safeAlt = (alt?.trim() || normalizedKey).replace(/"/g, '&quot;');
  const aiAttr = options?.aiGenerated ? ' data-ai-generated="true"' : '';
  return [
    `<!-- FIGURE_RASTER:key=${normalizedKey} -->`,
    `<img src="${safeSrc}" alt="${safeAlt}" data-figure-key="${normalizedKey}"${aiAttr} />`,
    `<!-- /FIGURE_RASTER -->`,
  ].join('\n');
}

export function collectRasterFiguresFromSteps(
  steps: InstructionStepDto[] | undefined
): StepRasterFigureDto[] {
  return (steps ?? []).flatMap(step => step.e251_RasterFigures ?? []);
}

export function findRasterFigureByKey(
  steps: InstructionStepDto[] | undefined,
  figureKey: string
): { stepId: string; figure: StepRasterFigureDto } | undefined {
  const target = normalizeFigureKey(figureKey).toLowerCase();
  if (!target) return undefined;
  for (const step of steps ?? []) {
    const stepId = step.e251_StepId ? String(step.e251_StepId) : undefined;
    if (!stepId) continue;
    for (const figure of step.e251_RasterFigures ?? []) {
      const key = normalizeFigureKey(figure.e251_FigureKey ?? '');
      if (key.toLowerCase() === target) {
        return { stepId, figure };
      }
    }
  }
  return undefined;
}

export function resolveRasterFigureImageSrc(figure: StepRasterFigureDto): string {
  const direct = figure.e251_ImageUrl?.trim();
  if (direct) return direct;

  const options = figure.e251_RasterOptions ?? [];
  const selected = figure.e251_SelectedOptionId
    ? options.find(option => option.e251_OptionId === figure.e251_SelectedOptionId)
    : options[0];
  const fromOption = selected?.e251_ImageUrl?.trim();
  if (fromOption) return fromOption;

  const blobId = selected?.e251_BlobId?.trim();
  return blobId ? `/api/GetBlob?id=${blobId}` : '';
}

function replaceFigureSlot(content: string, figureKey: string, replacement: string): string {
  const keyLower = figureKey.toLowerCase();

  PENDING_MARKER_RE.lastIndex = 0;
  for (const match of content.matchAll(PENDING_MARKER_RE)) {
    const key = normalizeFigureKey(match[1] ?? '');
    if (key.toLowerCase() !== keyLower || match.index == null) continue;
    return content.slice(0, match.index) + replacement + content.slice(match.index + match[0].length);
  }

  KEYED_SLOT_RE.lastIndex = 0;
  for (const match of content.matchAll(KEYED_SLOT_RE)) {
    const key = normalizeFigureKey(match[2] ?? '');
    if (key.toLowerCase() !== keyLower || match.index == null) continue;
    return content.slice(0, match.index) + replacement + content.slice(match.index + match[0].length);
  }

  return content;
}

/** Swap figure slots for ready raster image blocks already stored on the step. */
export function injectReadyRasterFiguresIntoMarkdown(
  content: string,
  figures: StepRasterFigureDto[] | undefined
): string {
  if (!content || !figures?.length) return content;

  let next = content;
  for (const figure of figures) {
    const key = normalizeFigureKey(figure.e251_FigureKey ?? '');
    const src = resolveRasterFigureImageSrc(figure);
    if (!key || !src) continue;
    next = replaceFigureSlot(
      next,
      key,
      buildRasterFigureMarkdownBlock(key, src, figure.e251_RasterTitle, {
        aiGenerated: isAiGeneratedRasterFigure(figure),
      })
    );
  }
  return next;
}

type LocatedBlock =
  | { kind: 'figure'; index: number; length: number; key: string; block: string }
  | { kind: 'raster'; index: number; length: number; key: string; block: string }
  | {
      kind: 'raster_slot';
      index: number;
      length: number;
      key: string;
      slotKind: RasterSlotKind;
      block: string;
    };

function collectLocatedBlocks(content: string): LocatedBlock[] {
  const blocks: LocatedBlock[] = [];
  FIGURE_BLOCK_RE.lastIndex = 0;
  RASTER_BLOCK_RE.lastIndex = 0;
  TEMP_SLOT_RE.lastIndex = 0;
  PENDING_MARKER_RE.lastIndex = 0;

  for (const match of content.matchAll(FIGURE_BLOCK_RE)) {
    blocks.push({
      kind: 'figure',
      index: match.index ?? 0,
      length: match[0].length,
      key: match[1]?.trim() ?? 'Fig. A',
      block: match[0],
    });
  }
  for (const match of content.matchAll(RASTER_BLOCK_RE)) {
    blocks.push({
      kind: 'raster',
      index: match.index ?? 0,
      length: match[0].length,
      key: match[1]?.trim() ?? 'Fig. A',
      block: match[0],
    });
  }
  for (const match of content.matchAll(TEMP_SLOT_RE)) {
    const kindRaw = (match[1] ?? '').toUpperCase();
    const slotKind: RasterSlotKind =
      kindRaw === 'AWAITING' ? 'awaiting' : kindRaw === 'EXTRACTION_FAILED' ? 'failed' : 'generating';
    blocks.push({
      kind: 'raster_slot',
      index: match.index ?? 0,
      length: match[0].length,
      key: match[2]?.trim() ?? 'Fig. A',
      slotKind,
      block: match[0],
    });
  }
  for (const match of content.matchAll(PENDING_MARKER_RE)) {
    blocks.push({
      kind: 'raster_slot',
      index: match.index ?? 0,
      length: match[0].length,
      key: match[1]?.trim() ?? 'Fig. A',
      slotKind: 'pending',
      block: match[0],
    });
  }

  return blocks.sort((a, b) => a.index - b.index);
}

/** Split markdown body into prose, SVG diagram blocks, and raster image / slot blocks. */
export function splitSupportingMaterialContent(content: string): SupportingMaterialContentSegment[] {
  if (!content?.trim()) return [];

  const segments: SupportingMaterialContentSegment[] = [];
  const blocks = collectLocatedBlocks(content);
  let lastIndex = 0;

  for (const located of blocks) {
    // Overlapping matches (should not happen) — skip if already consumed.
    if (located.index < lastIndex) continue;

    if (located.index > lastIndex) {
      const markdown = content.slice(lastIndex, located.index);
      if (markdown.trim()) {
        segments.push({ type: 'markdown', content: markdown });
      }
    }

    if (located.kind === 'figure') {
      const spec = extractHiddenSpec(located.block);
      const svg = extractSvg(located.block);
      segments.push({
        type: 'figure',
        key: located.key,
        spec,
        svg,
        failed: isFailedSvg(svg),
      });
    } else if (located.kind === 'raster') {
      const { src, alt, aiGenerated } = extractRasterImg(located.block);
      if (src) {
        segments.push({
          type: 'raster',
          key: located.key,
          src,
          alt: alt || located.key,
          aiGenerated,
        });
      } else {
        segments.push({
          type: 'raster_slot',
          key: located.key,
          kind: 'ready',
        });
      }
    } else {
      segments.push({
        type: 'raster_slot',
        key: located.key,
        kind: located.slotKind,
      });
    }

    lastIndex = located.index + located.length;
  }

  const tail = content.slice(lastIndex);
  if (tail.trim()) {
    segments.push({ type: 'markdown', content: tail });
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', content }];
}
